import React, { useState, useEffect, useCallback } from 'react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, isSameMonth, isSameDay,
  isToday, differenceInHours
} from 'date-fns';
import toast from 'react-hot-toast';
import api from '../utils/api';
import EventModal from '../components/EventModal';
import EventViewModal from '../components/EventViewModal';
import { AlertCircle } from 'lucide-react';
import './CalendarPage.css';

const TYPE_COLORS = {
  'HR Screening Call':           { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE', dot: '#3B82F6' },
  'HR Interview':                { bg: '#EEF2FF', text: '#4338CA', border: '#C7D2FE', dot: '#6366F1' },
  'Technical Interview':         { bg: '#F0FDF4', text: '#15803D', border: '#BBF7D0', dot: '#22C55E' },
  'Technical Exam / Assessment': { bg: '#FFFBEB', text: '#B45309', border: '#FDE68A', dot: '#F59E0B' },
  'Final Interview':             { bg: '#FDF4FF', text: '#7E22CE', border: '#E9D5FF', dot: '#A855F7' },
  'Offer Discussion':            { bg: '#ECFDF5', text: '#065F46', border: '#6EE7B7', dot: '#10B981' },
  'Background Check':            { bg: '#F0F9FF', text: '#0369A1', border: '#BAE6FD', dot: '#0EA5E9' },
  'Follow-up Call':              { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA', dot: '#F97316' },
  'Networking Call':             { bg: '#F0FAFE', text: '#0E7490', border: '#A5F3FC', dot: '#06B6D4' },
  'Other':                       { bg: '#F9FAFB', text: '#4B5563', border: '#D1D5DB', dot: '#9CA3AF' },
};

const urgencyLevel = (date) => {
  const hrs = differenceInHours(new Date(date), new Date());
  if (hrs < 0) return 'past';
  if (hrs < 24) return 'urgent';
  if (hrs < 72) return 'soon';
  return 'normal';
};


const getEventColors = (type, customTypes) => {
  if (TYPE_COLORS[type]) return TYPE_COLORS[type];
  const ct = customTypes.find(c => c.label === type);
  if (ct) return { bg: ct.color + '20', text: ct.color, border: ct.color + '60', dot: ct.color };
  return TYPE_COLORS['Other'];
};

const AgendaView = ({ events, customTypes, onEventClick }) => {
  const grouped = {};
  [...events]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .forEach(ev => {
      const key = format(new Date(ev.date), 'yyyy-MM-dd');
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(ev);
    });

  const days = Object.keys(grouped).sort();
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  if (days.length === 0) {
    return <div className="cal-agenda-empty">No events this month.</div>;
  }

  return (
    <div className="cal-agenda">
      {days.map(day => {
        const dayEvents = grouped[day];
        const dateObj = new Date(day + 'T12:00:00');
        const isThisToday = day === todayStr;
        return (
          <div key={day} className="cal-agenda-group">
            <div className={`cal-agenda-date-col${isThisToday ? ' cal-agenda-date-today' : ''}`}>
              <span className="cal-agenda-dow">{format(dateObj, 'EEE')}</span>
              <span className="cal-agenda-day-num">{format(dateObj, 'd')}</span>
              <span className="cal-agenda-month-label">{format(dateObj, 'MMM')}</span>
            </div>
            <div className="cal-agenda-events-col">
              {dayEvents.map(ev => {
                const colors = getEventColors(ev.type, customTypes);
                const urgency = urgencyLevel(ev.date);
                return (
                  <div
                    key={ev._id}
                    className={`cal-agenda-event-row urgency-${urgency}`}
                    style={{ borderLeftColor: colors.dot, background: colors.bg }}
                    onClick={() => onEventClick(ev)}
                  >
                    <div className="cal-agenda-event-time">{format(new Date(ev.date), 'h:mm a')}</div>
                    <div className="cal-agenda-event-body">
                      <div className="cal-agenda-event-title">{ev.title}</div>
                      <div className="cal-agenda-event-meta" style={{ color: colors.text }}>
                        {ev.type}{ev.job ? ` · ${ev.job.company}` : ''}
                      </div>
                    </div>
                    {urgency === 'urgent' && <span className="cal-event-urgent-badge">!</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CalendarPage = ({ jobs, onJobUpdate, onPrepare, onUpgrade, openNewEvent, onEventOpened }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [customTypes, setCustomTypes] = useState([]);
  const [usedTypes, setUsedTypes] = useState(null);
  const [events, setEvents] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [calView, setCalView] = useState('calendar');

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const monthStr = format(currentMonth, 'yyyy-MM');
      const res = await api.get(`/api/events?month=${monthStr}`);
      setEvents(res.data.events);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  }, [currentMonth]);

  const fetchUpcoming = useCallback(async () => {
    try {
      const res = await api.get('/api/events/upcoming');
      setUpcoming(res.data.events);
    } catch {}
  }, []);

  const fetchUsedTypes = useCallback(async () => {
    try {
      const res = await api.get('/api/events/types');
      setUsedTypes(res.data.types);
    } catch {}
  }, []);

  const fetchCustomTypes = useCallback(async () => {
    try {
      const res = await api.get('/api/profile/event-types');
      setCustomTypes(res.data.customEventTypes || []);
    } catch {}
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { fetchUpcoming(); }, [fetchUpcoming]);
  useEffect(() => { fetchUsedTypes(); }, [fetchUsedTypes]);
  useEffect(() => { fetchCustomTypes(); }, [fetchCustomTypes]);

  useEffect(() => {
    if (openNewEvent) {
      setEditingEvent(null);
      setShowModal(true);
      onEventOpened?.();
    }
  }, [openNewEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddEvent = async (data) => {
    try {
      const res = await api.post('/api/events', data);
      toast.success('Event added!');
      setShowModal(false);
      fetchEvents();
      fetchUpcoming();
      fetchUsedTypes();
      if (data.updateJobStage && data.job) {
        onJobUpdate(data.job, {
          ...(data.newStage && { stage: data.newStage }),
          ...(data.newStatus && { status: data.newStatus })
        });
      }
      return res.data.event;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add event');
      throw err;
    }
  };

  const handleUpdateEvent = async (id, data) => {
    try {
      await api.put(`/api/events/${id}`, data);
      toast.success('Event updated!');
      setEditingEvent(null);
      fetchEvents();
      fetchUpcoming();
      fetchUsedTypes();
    } catch (err) {
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      await api.delete(`/api/events/${id}`);
      toast.success('Event removed');
      setEditingEvent(null);
      fetchEvents();
      fetchUpcoming();
      fetchUsedTypes();
    } catch { toast.error('Failed to delete event'); }
  };

  // Build calendar grid
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days = [];
  let day = calStart;
  while (day <= calEnd) { days.push(day); day = addDays(day, 1); }

  const eventsOnDay = (d) =>
    events.filter(e => isSameDay(new Date(e.date), d));

  const handleDayClick = (d) => {
    setSelectedDate(d);
    setSelectedDayEvents(eventsOnDay(d));
  };

  return (
    <div className="cal-root">
      {/* Urgent banner */}
      {upcoming.filter(e => urgencyLevel(e.date) === 'urgent').length > 0 && (
        <div className="cal-urgent-banner">
          <span className="cal-urgent-icon"><AlertCircle size={15} /></span>
          <span>
            <strong>Heads up!</strong> You have{' '}
            {upcoming.filter(e => urgencyLevel(e.date) === 'urgent').length} event(s) happening within the next 24 hours:
            {' '}{upcoming.filter(e => urgencyLevel(e.date) === 'urgent').map(e => (
              <strong key={e._id}>{e.title}</strong>
            )).reduce((acc, el, i) => i === 0 ? [el] : [...acc, ', ', el], [])}
          </span>
        </div>
      )}

      <div className="cal-layout">
        {/* Main calendar */}
        <div className="cal-main">
          {loading && (
            <div className="cal-loading-overlay">
              <div className="cal-loading-spinner" />
              <span className="cal-loading-text">Loading your schedule...</span>
            </div>
          )}
          {/* Calendar header */}
          <div className="cal-header">
            <div className="cal-header-left">
              {/* Month selector */}
              <select
                className="cal-month-select"
                value={format(currentMonth, 'M')}
                onChange={e => {
                  const d = new Date(currentMonth);
                  d.setMonth(parseInt(e.target.value) - 1);
                  setCurrentMonth(d);
                }}
              >
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>

              {/* Year selector — ±5 years from current */}
              <select
                className="cal-year-select"
                value={format(currentMonth, 'yyyy')}
                onChange={e => {
                  const d = new Date(currentMonth);
                  d.setFullYear(parseInt(e.target.value));
                  setCurrentMonth(d);
                }}
              >
                {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 3 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              <button className="cal-today-btn" onClick={() => setCurrentMonth(new Date())}>Today</button>
            </div>
            <div className="cal-nav">
              <button className="cal-nav-btn" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button className="cal-nav-btn" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
              <button
                className={`cal-view-btn${calView === 'list' ? ' cal-view-btn-active' : ''}`}
                onClick={() => setCalView(calView === 'calendar' ? 'list' : 'calendar')}
                title={calView === 'calendar' ? 'Switch to list view' : 'Switch to calendar view'}
              >
                {calView === 'calendar' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                    <circle cx="3" cy="6" r="1"/><circle cx="3" cy="12" r="1"/><circle cx="3" cy="18" r="1"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
                    <line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/>
                  </svg>
                )}
                <span className="cal-view-btn-label">{calView === 'calendar' ? 'List' : 'Calendar'}</span>
              </button>
              <button className="cal-add-btn" onClick={() => { setEditingEvent(null); setShowModal(true); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Event
              </button>
            </div>
          </div>

          <div key={calView} className="cal-view-content">
          {calView === 'calendar' ? (
            <>
              {/* Day of week labels */}
              <div className="cal-weekdays">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                  <div key={d} className="cal-weekday">{d}</div>
                ))}
              </div>

              {/* Calendar grid — table layout keeps columns perfectly stable */}
              <div className="cal-grid">
                {Array.from({ length: Math.ceil(days.length / 7) }, (_, weekIdx) => (
                  <div key={weekIdx} className="cal-week-row">
                    {days.slice(weekIdx * 7, weekIdx * 7 + 7).map((d, i) => {
                      const dayEvents = eventsOnDay(d);
                      const isSelected = selectedDate && isSameDay(d, selectedDate);
                      const inMonth = isSameMonth(d, currentMonth);
                      const todayDay = isToday(d);
                      return (
                        <div
                          key={i}
                          className={[
                            'cal-cell',
                            !inMonth ? 'cal-cell-faded' : '',
                            todayDay ? 'cal-cell-today' : '',
                            isSelected ? 'cal-cell-selected' : '',
                          ].filter(Boolean).join(' ')}
                          onClick={() => handleDayClick(d)}
                        >
                          <span className="cal-cell-num">{format(d, 'd')}</span>
                          <div className="cal-cell-events">
                            {dayEvents.slice(0, 3).map(ev => {
                              const colors = getEventColors(ev.type, customTypes);
                              const urgency = urgencyLevel(ev.date);
                              return (
                                <div
                                  key={ev._id}
                                  className={`cal-event-chip urgency-${urgency}`}
                                  style={{ background: colors.bg, color: colors.text, borderColor: colors.border }}
                                  onClick={(e) => { e.stopPropagation(); setViewingEvent(ev); }}
                                  title={`${ev.title}${ev.job ? ` — ${ev.job.company}` : ''}`}
                                >
                                  <span className="cal-event-dot" style={{ background: colors.dot }} />
                                  <span className="cal-event-chip-text">
                                    {format(new Date(ev.date), 'h:mma')} {ev.title}
                                  </span>
                                  {urgency === 'urgent' && <span className="cal-event-urgent-badge">!</span>}
                                </div>
                              );
                            })}
                            {dayEvents.length > 3 && (
                              <div className="cal-event-more">+{dayEvents.length - 3} more</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <AgendaView
              events={events}
              customTypes={customTypes}
              onEventClick={setViewingEvent}
            />
          )}
          </div>
        </div>

        {/* Right sidebar — only in calendar view */}
        {calView === 'calendar' && <div className="cal-sidebar">
          {/* Upcoming events */}
          <div className="cal-sidebar-section">
            <h3 className="cal-sidebar-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              Next 7 Days
            </h3>
            {upcoming.length === 0 ? (
              <div className="cal-sidebar-empty">No upcoming events.</div>
            ) : (
              <div className="cal-upcoming-list">
                {upcoming.map(ev => {
                  const colors = getEventColors(ev.type, customTypes);
                  const urgency = urgencyLevel(ev.date);
                  return (
                    <div
                      key={ev._id}
                      className={`cal-upcoming-item urgency-item-${urgency}`}
                      onClick={() => setViewingEvent(ev)}
                    >
                      <div className="cal-upcoming-left">
                        <span className="cal-upcoming-dot" style={{ background: colors.dot }} />
                        <div>
                          <div className="cal-upcoming-title">{ev.title}</div>
                          {ev.job && (
                            <div className="cal-upcoming-company">{ev.job.company} · {ev.job.role}</div>
                          )}
                        </div>
                      </div>
                      <div className="cal-upcoming-right">
                        <div className="cal-upcoming-date">{format(new Date(ev.date), 'MMM d')}</div>
                        <div className="cal-upcoming-time">{format(new Date(ev.date), 'h:mm a')}</div>
                        {urgency === 'urgent' && <span className="urgency-badge-urgent">Today!</span>}
                        {urgency === 'soon' && <span className="urgency-badge-soon">Soon</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected day detail */}
          {selectedDate && (
            <div className="cal-sidebar-section">
              <h3 className="cal-sidebar-title">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
                  <line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/>
                </svg>
                {format(selectedDate, 'EEEE, MMM d')}
              </h3>
              {selectedDayEvents.length === 0 ? (
                <div className="cal-sidebar-empty">
                  Nothing scheduled.
                  <button className="cal-day-add-btn" onClick={() => { setEditingEvent(null); setShowModal(true); }}>
                    + Add event
                  </button>
                </div>
              ) : (
                <div className="cal-day-events-list">
                  {selectedDayEvents.map(ev => {
                    const colors = getEventColors(ev.type, customTypes);
                    return (
                      <div key={ev._id} className="cal-day-event"
                        style={{ borderLeftColor: colors.dot }}
                        onClick={() => setEditingEvent(ev)}
                      >
                        <div className="cal-day-event-time">{format(new Date(ev.date), 'h:mm a')}</div>
                        <div className="cal-day-event-title">{ev.title}</div>
                        <div className="cal-day-event-type" style={{ color: colors.text }}>{ev.type}</div>
                        {ev.job && <div className="cal-day-event-job">{ev.job.company}</div>}
                        {ev.meetingLink && (
                          <a href={ev.meetingLink} target="_blank" rel="noreferrer" className="cal-day-event-link"
                            onClick={e => e.stopPropagation()}>
                            Join meeting ↗
                          </a>
                        )}
                        {ev.venue && <div className="cal-day-event-venue">{ev.venue}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Type legend — only shows types actually used in schedule */}
          <div className="cal-sidebar-section cal-legend">
            <h3 className="cal-sidebar-title">Event Types</h3>
            <div className="cal-legend-list">
              {Object.entries(TYPE_COLORS)
                .filter(([k]) => usedTypes === null ? k !== 'Other' : usedTypes.includes(k))
                .map(([type, colors]) => (
                  <div key={type} className="cal-legend-item">
                    <span className="cal-legend-dot" style={{ background: colors.dot }} />
                    <span>{type}</span>
                  </div>
                ))}
              {customTypes
                .filter(ct => !usedTypes || usedTypes.includes(ct.label))
                .map(ct => (
                  <div key={ct.label} className="cal-legend-item">
                    <span className="cal-legend-dot" style={{ background: ct.color }} />
                    <span>{ct.label}</span>
                  </div>
                ))}
              {usedTypes !== null && usedTypes.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '2px 0' }}>No events yet.</div>
              )}
            </div>
          </div>
        </div>}
      </div>

      {viewingEvent && (
        <EventViewModal
          event={viewingEvent}
          jobs={jobs}
          customTypes={customTypes}
          onCustomTypesChange={setCustomTypes}
          onClose={() => setViewingEvent(null)}
          onUpdate={async (id, data) => { await handleUpdateEvent(id, data); setViewingEvent(null); }}
          onDelete={async (id) => { await handleDeleteEvent(id); setViewingEvent(null); }}
          onPrepare={(ev) => { setViewingEvent(null); onPrepare && onPrepare(ev); }}
          onUpgrade={onUpgrade}
          onReminderToggle={(updatedEvent) => {
            setViewingEvent(updatedEvent);
            fetchEvents();
            fetchUpcoming();
          }}
        />
      )}

      {(showModal || editingEvent) && (
        <EventModal
          event={editingEvent}
          jobs={jobs}
          defaultDate={selectedDate}
          customTypes={customTypes}
          onCustomTypesChange={setCustomTypes}
          onClose={() => { setShowModal(false); setEditingEvent(null); }}
          onAdd={handleAddEvent}
          onUpdate={handleUpdateEvent}
          onDelete={handleDeleteEvent}
          onUpgrade={onUpgrade}
        />
      )}
    </div>
  );
};

export default CalendarPage;
